import { create } from "zustand";
import { useAuth } from "./auth";

type Theme = "light" | "dark";

interface AppState {
  theme: Theme;
  paletteOpen: boolean;
  aiPanelOpen: boolean;
  /** 派生：是否已登录（基于 useAuth.token） */
  authed: boolean;
  /** 兼容旧 API：仅本地切换"已登录态"（实际 token 由 useAuth 持有） */
  login: () => void;
  logout: () => void;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
  setPaletteOpen: (open: boolean) => void;
  setAiPanelOpen: (open: boolean) => void;
}

const STORAGE_KEY = "lumeseek_web_state_v1";

interface PersistedSlice {
  theme: Theme;
}

function loadPersisted(): PersistedSlice {
  if (typeof window === "undefined") return { theme: "light" };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { theme: "light" };
    const parsed = JSON.parse(raw);
    return {
      theme: parsed.theme === "dark" ? "dark" : "light",
    };
  } catch {
    return { theme: "light" };
  }
}

function persist(slice: PersistedSlice) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(slice));
  } catch {
    // ignore
  }
}

const initial = loadPersisted();

function applyTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.style.colorScheme = theme;
}

export const useAppStore = create<AppState>((set, get) => ({
  theme: initial.theme,
  paletteOpen: false,
  aiPanelOpen: true,
  authed: !!useAuth.getState().token,
  login: () => {
    // login 由 LoginPage 通过 useAuth.setAuth 完成；这里仅同步 UI 标记
    set({ authed: true });
  },
  logout: () => {
    useAuth.getState().logout();
    set({ authed: false });
  },
  toggleTheme: () => {
    const next: Theme = get().theme === "dark" ? "light" : "dark";
    set({ theme: next });
    applyTheme(next);
    persist({ theme: next });
  },
  setTheme: (t) => {
    set({ theme: t });
    applyTheme(t);
    persist({ theme: t });
  },
  setPaletteOpen: (open) => set({ paletteOpen: open }),
  setAiPanelOpen: (open) => set({ aiPanelOpen: open }),
}));

// 让 useAuth 的变化反向同步到 useAppStore.authed
useAuth.subscribe((state) => {
  const authed = !!state.token;
  if (useAppStore.getState().authed !== authed) {
    useAppStore.setState({ authed });
  }
});

export function applyInitialTheme() {
  applyTheme(initial.theme);
}
